import { Box, Button, Group, Text } from "@mantine/core"
import { DownloadSimpleIcon } from "@phosphor-icons/react"
import { DataTable } from "mantine-datatable"
import React, { useEffect, useMemo, useState } from "react"
import { TablePagination } from "./TablePagination"

const DEFAULT_PER_PAGE = 10

const tableStyle = {
  border: '1px solid #0000001A',
  borderRadius: '8px',
  overflow: 'hidden',
  flex: '1',
  minWidth: 0,
  width: '100%',
}

export type ReportColumn = { headerName: string; field: string }
export type ReportCell = string | number | null
export type ReportRow = Record<string, ReportCell>
export type ReportData = { columns: ReportColumn[]; rows: ReportRow[]; sum: ReportRow[] }

interface Props {
  data: ReportData
  onDownload?: () => void
  isDownloading?: boolean
  downloadError?: string | null
}

export const ReportSummaryTable: React.FC<Props> = ({ data, onDownload, isDownloading, downloadError }) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  useEffect(() => {
    setPage(1)
  }, [data])

  const total = data.rows.length
  const pageCnt = Math.max(1, Math.ceil(total / perPage))

  const tableColumns = useMemo(() =>
    data.columns.map((col) => {
      return {
        accessor: col.field,
        title: col.headerName,
        ellipsis: true,
        cellsStyle: (): React.CSSProperties => ({
          textAlign: col.headerName === "日付" ? undefined : "right",
        }),
      }
    }), [data.columns])

  const sumTableColumns = useMemo(() => {
    if (tableColumns.length === 0) return tableColumns
    return tableColumns.map((col, i) => (i === 0 ? { ...col, title: '' } : col))
  }, [tableColumns])

  const pagedRows = useMemo(() => {
    const from = (page - 1) * perPage
    return data.rows.slice(from, from + perPage)
  }, [page, perPage, data.rows])

  // 行数が少ないときにテーブル下部の余白が出ないよう、内部スクロールが必要な件数のときだけ
  // 高さを固定する。0件時は「データがありません」の表示領域を確保する。
  // （maxHeight は内部の ScrollArea が flex アイテムで縮まないため機能しない）
  const tableHeight = pagedRows.length === 0 ? 400 : pagedRows.length >= 15 ? 600 : undefined

  return (
    <>
      {tableColumns.length > 0 && (
        <Box mt={40} style={tableStyle}>
          <DataTable
            withColumnBorders
            records={data.sum}
            noRecordsText="データがありません"
            styles={{
              root: { border: 'none' },
              header: { backgroundColor: '#F8F9FA' }
            }}
            columns={sumTableColumns}
          />
        </Box>
      )}
      <Box mt={16} style={tableStyle}>
        <DataTable
          withColumnBorders
          records={pagedRows}
          noRecordsText="データがありません"
          height={tableHeight}
          styles={{
            root: { border: 'none', },
            header: { backgroundColor: '#F8F9FA' }
          }}
          columns={tableColumns}
        />

        <TablePagination
          page={page}
          onChangePage={setPage}
          perPage={perPage}
          onChangePerPage={(v) => { setPerPage(v); setPage(1) }}
          totalPages={pageCnt}
          isEmpty={pagedRows.length === 0}
        />
      </Box>
      <Group mt={16} justify='end'>
        <Button
          variant="outline"
          radius="xl"
          onClick={onDownload}
          disabled={!onDownload || isDownloading}
          leftSection={<DownloadSimpleIcon size={16} weight="bold" />}
        >
          {isDownloading ? '処理中です。しばらくお待ちください' : '詳細情報ダウンロード'}
        </Button>
      </Group>
      {downloadError && (
        <Text c="red" fz={13} mt={8} ta="end" style={{ whiteSpace: 'pre-wrap' }}>
          {downloadError}
        </Text>
      )}
    </>
  )
}
